import React, { useState, useEffect } from 'react';
import RichTable from '../components/richTable/RichTable';
import TradeDetailsDrawer from './TradeDetailsDrawer';
import AlgoDrawer from '../algos/AlgoDrawer';
import CandleStickChart from '../stock/CandlestickChart';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import IndicatorSelector from '../stock/IndicatorSelector';

function NoteEditor({ initialNote, onSave }) {
    const [text, setText] = useState(initialNote || '');

    return (
        <>
            <textarea rows={3} cols={70} placeholder="Notes" onChange={(e) => setText(e.target.value)} value={text} />
            <br></br>
            <button onClick={() => onSave(text)}>Save Note</button>
        </>
    );
}

function TradeNotes(props) {
    const [notes, setNotes] = useState([]);
    const [error, setError] = useState(null);
    const [updates, setUpdates] = useState(0);
    const [activeTrades, setActiveTrades] = useState([]);
    const [completedTrades, setCompletedTrades] = useState([]);
    const [indicatorOptions, setIndicatorOptions] = useState([]);
    const [patterns, setPatterns] = useState([]);
    const [selectedIndicators, setSelectedIndicators] = useState(() => {
        const saved = localStorage.getItem('tradeReviewIndicators');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                return [];
            }
        }
        return [];
    });

    useEffect(() => {
        localStorage.setItem('tradeReviewIndicators', JSON.stringify(selectedIndicators));
    }, [selectedIndicators]);

    useEffect(() => {
        (async function () {
            try {
                const options = await (await fetch(`/api/indicators`)).json();
                setIndicatorOptions(options.indicators || []);
                setPatterns(options.patterns || []);
            } catch (e) {
                console.error("Failed to fetch indicators", e);
            }
        })();
    }, []);

    useEffect(() => {
        if (!props.userId) return;

        (async function () {
            try {
                const url = `/api/tradeNotes?userId=${props.userId}`;
                const response = await fetch(url);
                const data = await response.json();
                console.log("Trade Notes", data);

                if (data.success) {
                    setNotes(data.notes);
                } else {
                    setError(data.error);
                }
            } catch (e) {
                setError(e.message);
            }
        })();
    }, [props.userId, updates]);

    useEffect(() => {
        (async function () {
            try {
                const url = `/api/activeTrades?userId=${props.userId}&planId=${props.planId}`;
                const response = await fetch(url);
                const data = await response.json();
                console.log("Active Trades", data);
                setActiveTrades(data);

                let startDate = new Date();
                startDate.setDate(startDate.getDate() - 3);
                const completedTradesUrl = `/api/completedTrades?userId=${props.userId}&planId=${props.planId}&startDate=` + startDate.toLocaleDateString() + "&endDate=" + new Date().toLocaleDateString();
                const completedTradesResponse = await fetch(completedTradesUrl);
                const completedTradesData = await completedTradesResponse.json();
                console.log("Completed Trades", completedTradesData);
                setCompletedTrades(completedTradesData);
            } catch (e) {
                setError(e.message);
            }
        })();
    }, [props.planId, props.userId, updates]);


    const deleteNote = async (planTradeId) => {
        const url = `/api/tradeNote?planTradeId=${planTradeId}&userId=${props.userId}&note=`;
        const response = await fetch(url, { method: 'POST' });
        const data = await response.json();
        console.log("Delete Note", data);

        if (data.success) {
            setUpdates(updates + 1)
        } else {
            setError(data.error);
        }
    }

    function renderChartForActiveTrade(cell, row) {
        let trades = [{
            purchase_date: row.purchase_date,
            purchase_price: row.purchase_price,
            sell_date: row.sell_date,
        }]

        return (
            <CandleStickChart showMovingAverage={false} showAveragePrice={true} symbol={row.symbol} trades={trades} indicators={selectedIndicators} width={500} height={400} />
        )
    }

    async function saveNote(planTradeId, noteText) {
        updateNote(planTradeId, noteText);
        const url = `/api/tradeNote?planTradeId=${planTradeId}&userId=${props.userId}&note=${encodeURIComponent(noteText)}`;
        const text = await (await fetch(url, { method: 'POST' })).json();
        console.log(text);
        setUpdates(updates + 1);
    }

    function updateNote(planTradeId, noteText) {
        let currentNotes = JSON.parse(JSON.stringify(notes));
        let note = currentNotes.find(n => n.planTradeId === planTradeId);

        if (note) {
            note.note = noteText;
        } else {
            let trade = activeTrades.find(t => t.idplan_trades === planTradeId);
            if (trade === undefined) {
                trade = completedTrades.find(t => t.idplan_trades === planTradeId);
            }

            currentNotes.push({
                planTradeId,
                note: noteText,
                trade_symbol: trade ? trade.trade_symbol : '',
                algoId: trade ? trade.algoId : -1,
                display_name: trade ? trade.display_name : '',
                reviewed: false
            });
        }

        setNotes(currentNotes);
    }

    function renderAlgoInfo(algoId, row) {
        let note = notes.find(n => n.planTradeId === row.idplan_trades);
        let noteText = note ? note.note : '';
        return <>
            Reviewed: {note && note.reviewed ? <CheckIcon /> : <CloseIcon />}<br />
            Symbol: {row.trade_symbol}<br />
            {row.sell_date ? "Completed: " + new Date(row.sell_date).toLocaleDateString() : null}<br />
            <AlgoDrawer link={row.display_name} planId={props.planId} accountId={props.accountId} userId={props.userId} mobileView={props.mobileView} algoId={row.algoId} role={props.role} />
            <TradeDetailsDrawer mobileView={props.mobileView} userId={props.userId} planTradeId={row.idplan_trades} link={"Details"} role={props.role} />
            <NoteEditor initialNote={noteText} onSave={(text) => saveNote(row.idplan_trades, text)} />
        </>
    }

    function getUnReviewedCompletedTrades() {
        return completedTrades;
    }

    const columns = [
        {
            title: 'Symbol',
            field: 'trade_symbol',
            formatter: (value, rowData) => (
                <TradeDetailsDrawer
                    link={rowData.trade_symbol || 'Details'}
                    userId={props.userId}
                    planTradeId={rowData.planTradeId}
                    mobileView={props.mobileView}
                    role={props.role}
                />
            )
        },
        {
            title: 'Algo',
            field: 'display_name',
            formatter: (value, rowData) => (
                <AlgoDrawer
                    link={rowData.display_name || 'Details'}
                    planId={props.planId}
                    accountId={props.accountId}
                    userId={props.userId}
                    mobileView={props.mobileView}
                    algoId={rowData.idalgos}
                    role={props.role} />
            )
        },
        {
            title: 'Note',
            field: 'note'
        },
        {
            title: '',
            field: '',
            formatter: (value, rowData) => (
                <button onClick={() => deleteNote(rowData.planTradeId)}>Delete</button>
            )
        }
    ];

    if (error) {
        return <div style={{ color: 'red' }}>Error: {error}</div>;
    }

    return (
        <div>
            <div>
                <h4>Indicators</h4>
                <IndicatorSelector indicators={indicatorOptions} selectedIndicators={selectedIndicators} setSelectedIndicators={setSelectedIndicators} patterns={patterns} />
            </div>
            <br />
            <div>
                <h4>Open Notes</h4>
                <RichTable
                    mappers={columns}
                    data={notes.filter(note => note.note !== "")}
                />
            </div>

            <br></br>
            <div>
                <h4>Active Trades</h4>
                <RichTable
                    mappers={[
                        { title: 'Chart', field: 'trade_symbol', formatter: renderChartForActiveTrade, width: 400 },
                        { title: 'Info', field: 'algoId', formatter: renderAlgoInfo }
                    ]}
                    data={activeTrades}
                />
            </div>

            <br></br>
            <div>
                <h4>Recently Completed Trades</h4>
                <RichTable
                    mappers={[
                        { title: 'Chart', field: 'trade_symbol', formatter: renderChartForActiveTrade, width: 400 },
                        { title: 'Info', field: 'algoId', formatter: renderAlgoInfo }
                    ]}
                    data={getUnReviewedCompletedTrades()}
                />
            </div>
        </div>

    );
}

export default TradeNotes;
